import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

export interface Notification {
  id: string;
  headline: string;
  content: string;
  active: boolean;
  notify: boolean;
  validFrom: string;
  validUntil: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly STORAGE_KEY = 'notifications';
  private readonly READ_STATUS_KEY = 'read-status';

  constructor(private http: HttpClient) {}

  /**
   * Fetch all notifications from the JSON file
   */
  getNotifications(): Observable<Notification[]> {
    return this.http.get<Notification[]>('/ui/assets/notifications.json').pipe(
      catchError(error => {
        console.error('Error loading notifications:', error);
        return of([]);
      })
    );
  }

  /**
   * Get unread notifications that meet all display criteria
   */
  getUnreadNotifications(): Observable<Notification[]> {
    return this.getNotifications().pipe(
      map(notifications => {
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        return notifications
          .filter(notification => {
            // Check active and notify flags
            if (!notification.active || !notification.notify) {
              return false;
            }

            // Check date validity
            const validFrom = new Date(notification.validFrom);
            const validUntil = new Date(notification.validUntil);
            validFrom.setHours(0, 0, 0, 0);
            validUntil.setHours(23, 59, 59, 999);

            if (currentDate < validFrom || currentDate > validUntil) {
              return false;
            }

            // Check if already read
            if (this.isNotificationRead(notification.id)) {
              return false;
            }

            return true;
          })
          .slice(0, 3); // Take only last 3
      })
    );
  }

  /**
   * Get active notifications regardless of read status (for About dialog)
   */
  getActiveNotifications(): Observable<Notification[]> {
    return this.getNotifications().pipe(
      map(notifications => {
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        return notifications
          .filter(notification => {
            // Check active flag
            if (!notification.active) {
              return false;
            }

            // Check date validity
            const validFrom = new Date(notification.validFrom);
            const validUntil = new Date(notification.validUntil);
            validFrom.setHours(0, 0, 0, 0);
            validUntil.setHours(23, 59, 59, 999);

            if (currentDate < validFrom || currentDate > validUntil) {
              return false;
            }

            return true;
          })
          .slice(0, 3); // Take only last 3
      })
    );
  }

  /**
   * Check if a notification has been read
   */
  isNotificationRead(notificationId: string): boolean {
    const storedData = localStorage.getItem(this.STORAGE_KEY);

    if (!storedData) {
      return false;
    }

    try {
      const notifications = JSON.parse(storedData);
      return notifications[notificationId]?.[this.READ_STATUS_KEY]?.read === true;
    } catch (error) {
      console.error('Error parsing notification read status:', error);
      return false;
    }
  }

  /**
   * Mark a notification as read
   */
  markAsRead(notificationId: string): void {
    try {
      const storedData = localStorage.getItem(this.STORAGE_KEY);
      const notifications = storedData ? JSON.parse(storedData) : {};
      
      notifications[notificationId] = {
        [this.READ_STATUS_KEY]: {
          read: true,
          timestamp: new Date().toISOString()
        }
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(notifications));
    } catch (error) {
      console.error('Error saving notification read status:', error);
    }
  }

  /**
   * Mark multiple notifications as read
   */
  markMultipleAsRead(notificationIds: string[]): void {
    notificationIds.forEach(id => this.markAsRead(id));
  }

  /**
   * Clear all notification read statuses (for testing purposes)
   */
  clearAllReadStatuses(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing notification read statuses:', error);
    }
  }
}
